import { z } from 'zod';
import type { RegisterRequestToken } from '@sleed/bizcommerce-api';

/**
 * Register-request edit form. Mirrors `RegisterRequestToken` and the legacy admin grid popup
 * (Core/client/admin/src/pages/Customers/RegisterRequests/index.js): email required + ≤255,
 * name/surname/irs ≤50, company/descriptive-title ≤100, vat/tel/mob/fax ≤20, address ≤200,
 * gender (gd) is the EnumToken id, dob optional date, rnl/popt booleans. createDate (cdt) is
 * read-only. Records are created by the storefront register flow — the admin grid only edits/deletes.
 */
export const registerRequestSchema = z.object({
	id: z.number().optional(),
	eml: z.string().trim().min(1, 'validation.required').max(255, 'validation.tooLong').email('validation.email'),
	fn: z.string().trim().max(50, 'validation.tooLong').nullable(),
	ln: z.string().trim().max(50, 'validation.tooLong').nullable(),
	cnm: z.string().trim().max(100, 'validation.tooLong').nullable(),
	dtl: z.string().trim().max(100, 'validation.tooLong').nullable(),
	vat: z.string().trim().max(20, 'validation.tooLong').nullable(),
	irs: z.string().trim().max(50, 'validation.tooLong').nullable(),
	tel: z.string().trim().max(20, 'validation.tooLong').nullable(),
	mob: z.string().trim().max(20, 'validation.tooLong').nullable(),
	fax: z.string().trim().max(20, 'validation.tooLong').nullable(),
	adl: z.string().trim().max(200, 'validation.tooLong').nullable(),
	gd: z.number().nullable(),
	dob: z.string().nullable(),
	rnl: z.boolean(),
	popt: z.boolean()
});

export type RegisterRequestFormValues = z.infer<typeof registerRequestSchema>;

export function tokenToForm(token: RegisterRequestToken): RegisterRequestFormValues {
	return {
		id: token.id,
		eml: token.eml ?? '',
		fn: token.fn ?? '',
		ln: token.ln ?? '',
		cnm: token.cnm ?? '',
		dtl: token.dtl ?? '',
		vat: token.vat ?? '',
		irs: token.irs ?? '',
		tel: token.tel ?? '',
		mob: token.mob ?? '',
		fax: token.fax ?? '',
		adl: token.adl ?? '',
		gd: token.gd ?? null,
		dob: (token.dob as string | null) ?? null,
		rnl: token.rnl ?? false,
		popt: token.popt ?? false
	};
}

/**
 * Build the update payload. Spread the original token first so server-owned fields not on the
 * form (cdt/udt/si/iv/etc.) survive the round-trip — parity with the customers update path.
 */
export function formToToken(form: RegisterRequestFormValues, original: RegisterRequestToken | null): RegisterRequestToken {
	return {
		...(original ?? {}),
		id: form.id,
		eml: form.eml.trim(),
		fn: form.fn?.trim() || null,
		ln: form.ln?.trim() || null,
		cnm: form.cnm?.trim() || null,
		dtl: form.dtl?.trim() || null,
		vat: form.vat?.trim() || null,
		irs: form.irs?.trim() || null,
		tel: form.tel?.trim() || null,
		mob: form.mob?.trim() || null,
		fax: form.fax?.trim() || null,
		adl: form.adl?.trim() || null,
		gd: form.gd ?? undefined,
		dob: form.dob,
		rnl: form.rnl,
		popt: form.popt
	};
}
